// Import package components
import { useContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import classnames from "classnames";
import { Flex, Image } from "uikit-react";
import { useCookies } from "react-cookie";

import TeamAdd from "./add";
import TeamDelete from "./delete";
import BulkHours from "./bulk_hours";
import TableLoading from "../common/Loading/table_loading";
import { Link } from "../../ui";
import { AppContexts } from "../../providers";
import Pagination from "../common/Pagination";
import RecordsPerPage from "../common/RecordsPerPage";

const Team = () => {
  const [criteria, setCriteria] = useState({
    page: 0,
    search_term: "",
    sort: "updated_on",
  });
  const [showDeletedUsers, setShowDeletedUsers] = useState(false);
  const { set, settings, loading, active } = useContext(
    AppContexts.SettingsContext,
  );
  const { zoneGroups, getZoneGroups, toggleZoneItem } = useContext(
    AppContexts.ZonesContext,
  );

  const inboxCtx = useContext(AppContexts.InboxContext);

  const [dirty, setDirty] = useState(false);
  const [rec, setRec] = useState(null);
  const [inbox, setInbox] = useState(null);

  const [edits, setEdits] = useState({});
  const [deletes, setDeletes] = useState({});

  const [selectedRecipients, setSelectedRecipients] = useState([]);

  const [pendingZones, setPendingZones] = useState({});
  const [pendingRestrictions, setPendingRestrictions] = useState({
    known: [],
    "ignore-webchat": [],
  });

  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage = 20 } = cookies;
  const wasRecipientPending = useRef(false);

  useEffect(() => {
    document.title = "Users - RueBaRue";
    if (!!inboxCtx.settings) {
      setInbox(inboxCtx.settings);
    }
  }, [inboxCtx.settings, inboxCtx.loading]);

  useEffect(() => {
    getZoneGroups(["inboxes"]);
  }, []);

  useEffect(() => {
    inboxCtx.get({ include_deleted: showDeletedUsers });
  }, [showDeletedUsers]);

  useEffect(() => {
    if (
      wasRecipientPending.current &&
      !inboxCtx.recipientPending &&
      inboxCtx.deletedRecipient
    ) {
      inboxCtx.get({ include_deleted: showDeletedUsers });
    }

    wasRecipientPending.current = inboxCtx.recipientPending;
  }, [inboxCtx.deletedRecipient, inboxCtx.get, inboxCtx.recipientPending, showDeletedUsers]);

  const recipients = useMemo(() => {
    if (!inbox) {
      return [];
    } else {
      if (!inbox.recipients) {
        return [];
      } else {
        const pendingDeletedRecipientId =
          !showDeletedUsers &&
          inboxCtx.recipientPending &&
          inboxCtx.deletedRecipient?.recipient_id
            ? inboxCtx.deletedRecipient.recipient_id
            : null;

        return inbox.recipients
          .filter(
            (recipient) => recipient.recipient_id !== pendingDeletedRecipientId,
          )
          .filter((recipient) => showDeletedUsers || !recipient.deleted)
          .filter((recipient) => {
            if (
              [recipient.first_name, recipient.last_name]
                .join(" ")
                .toLowerCase()
                .search(criteria.search_term.toLowerCase()) >= 0
            )
              return true;
            return false;
          })
          .sort((prev, next) => {
            if (criteria.sort === "updated_on")
              return next.updated_at?.localeCompare(prev.updated_at);
            if (criteria.sort === "first_name")
              return prev.first_name?.localeCompare(next.first_name);
            if (criteria.sort === "last_name")
              return prev.last_name?.localeCompare(next.last_name);
            return true;
          });
      }
    }
  }, [inbox, criteria, showDeletedUsers, inboxCtx.deletedRecipient, inboxCtx.recipientPending]);

  const zoneInboxes = useMemo(() => {
    return (zoneGroups?.inboxes || []).reduce((a, c) => {
      a[c.id] = (c.personal_inbox_ids || []).map((id) => +id);

      return a;
    }, {});
  }, [zoneGroups]);

  const zoneCategories = useMemo(() => {
    return (zoneGroups?.categories || []).reduce((a, c) => {
      a[c.id] = (c.categories || []).map((id) => id);

      return a;
    }, {});
  }, [zoneGroups]);

  const pagedRecipients = useMemo(
    () =>
      recipients.slice(
        criteria.page * recordsPerPage,
        (criteria.page + 1) * recordsPerPage,
      ),
    [recipients, criteria, recordsPerPage],
  );

    const isSelected = (recipientId) =>
    selectedRecipients.includes(recipientId);

  const toggleRecipientSelection = (recipientId) => {
    setSelectedRecipients((prev) =>
      prev.includes(recipientId)
        ? prev.filter((id) => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  const toggleSelectAllOnPage = () => {
    const pageIds = pagedRecipients
      .filter((r) => !r.deleted)
      .map((r) => r.recipient_id);

    const allSelected = pageIds.every((id) => selectedRecipients.includes(id));

    setSelectedRecipients((prev) =>
      allSelected
        ? prev.filter((id) => !pageIds.includes(id))
        : [...new Set([...prev, ...pageIds])]
    );
  };

  const getSchedule = (schedule, day) => {
    let dow = (schedule || []).filter((wd) => !!wd && wd.weekday === day)[0];

    if (day === 1) {
      // console.log(dow);
    }

    if (!dow) {
      return "Off";
    } else {
      let st = new Date(
        1970,
        0,
        1,
        ...dow.start_time.split(/[\s|T]/)[1].split(":")
      ).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      let et = new Date(
        1970,
        0,
        1,
        ...dow.end_time.split(/[\s|T]/)[1].split(":")
      ).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
      return `${st}\n- ${et}`;
    }
  };

  const formatDay = (day) => ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][day - 1];

  const formatRange = (schedule, day) => {
    const dow = (schedule || []).find((wd) => wd?.weekday === day);
    if (!dow) return "Off";

    const st = moment(dow.start_time.split(/[\s|T]/)[1], "HH:mm:ss").format("h:mmA");
    const et = moment(dow.end_time.split(/[\s|T]/)[1], "HH:mm:ss").format("h:mmA");
    return `${st}–${et}`;
  };

  const summarizeSchedule = (schedule) => {
    const groups = {};
    for (let day = 1; day <= 7; day++) {
      const range = formatRange(schedule, day);
      if (!groups[range]) groups[range] = [];
      groups[range].push(formatDay(day));
    }

    return Object.entries(groups).map(([range, days]) => ({
      range,
      days,
      label:
        days.length === 7
          ? "Every day"
          : days.length === 5 && days.join(",") === "Mon,Tue,Wed,Thu,Fri"
            ? "Weekdays"
            : days.length === 2 && days.join(",") === "Sat,Sun"
              ? "Weekends"
              : days.join(", "),
    }));
  };

  const handleIndexChanged = useCallback((newPageIndex) => {
    handleCriteriaChange("page", newPageIndex);
  });

  const handleAdd = () => {
    inboxCtx.initRecipient();
    inboxCtx.setIsNew(true);
  };

  const handleEdit = (r) => {
    inboxCtx.setRecipient(JSON.parse(JSON.stringify(r)));
    inboxCtx.setIsNew(false);
  };

  const handleAlertToggle = (e, r) => {
    inboxCtx.updateRecipient({
      ...r,
      alerts: e.target.checked,
    });
  };

  const handleDelete = (r) => {
    inboxCtx.setDeletedRecipient(r);
  };

  const handleUnDelete = (r) => {
    inboxCtx.setDeletedRecipient(r);
    inboxCtx.undeleteRecipient(r);
  };

  const handleInvite = (r) => {
    setRec(r);
    inboxCtx.inviteRecipient(r);
  };

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((criteria) => ({ ...criteria, [key]: value }));
  });

  const getTeamSchedule = (dow) => {
    let min = null;
    let max = null;

    (inbox?.recipients || []).map((r) => {
      if (r.deleted) {
        return;
      }

      (r.schedule || []).map((wd) => {
        if (wd?.weekday === dow) {
          let st = moment(wd.start_time.split(/[\s|T]/)[1], "HH:mm:ss");
          let et = moment(wd.end_time.split(/[\s|T]/)[1], "HH:mm:ss");

          if (!min || st.isBefore(min)) {
            min = st;
          }

          if (!max || et.isAfter(max)) {
            max = et;
          }
        }
      });
    });

    if (!!min && !!max) {
      return `${min.format("h:mma")} - ${max.format("h:mma")}`;
    } else {
      return "Off";
    }
  };

function groupAndSummarize() {
  const schedule = [
    { day: "Sun", time: getTeamSchedule(1) },
    { day: "Mon", time: getTeamSchedule(2) },
    { day: "Tue", time: getTeamSchedule(3) },
    { day: "Wed", time: getTeamSchedule(4) },
    { day: "Thu", time: getTeamSchedule(5) },
    { day: "Fri", time: getTeamSchedule(6) },
    { day: "Sat", time: getTeamSchedule(7) },
  ];

  // Reverse time ranges
  const reversed = schedule.map(({ day, time }) => {
    if (time === "Off") {
      return { day, reversedTime: "All day" };
    }

    const [start, end] = time.split(" - ");
    return { day, reversedTime: `${end}–${start}` }; // en dash
  });

  // Group days by reversed time
  const grouped = {};
  reversed.forEach(({ day, reversedTime }) => {
    if (!grouped[reversedTime]) grouped[reversedTime] = [];
    grouped[reversedTime].push(day);
  });

  // Build readable summary
  const parts = Object.entries(grouped).map(([timeRange, days]) => {
    const dayLabel =
      days.length === 1
        ? days[0]
        : `${days[0]}–${days[days.length - 1]}`;

    if (timeRange === "All day") {
      return `${dayLabel} (all day)`;
    }

    return `${dayLabel} ${timeRange}`;
  });

  return parts.join(", ");
}

  const handleZoneToggle = () => {
    getZoneGroups(["inboxes", "categories"]);
  };

  const handleRestrictionToggle = (updated) => {
    if (!inbox || !updated?.personal_inbox) {
      return;
    }

    (inbox.recipients || []).map((r) => {
      (r.personal_inboxes || []).map((i) => {
        if (i.personal_inbox_id === updated.personal_inbox.id) {
          i.restrictions = [...updated.personal_inbox.restrictions];
        }
      });
    });

    inboxCtx.setInbox({ ...inbox });
  };

  const toggleZone = async (zone, type, item_id) => {
    if ((pendingZones[zone.id] || []).includes(item_id)) {
      return;
    }

    setPendingZones({
      ...pendingZones,
      [zone.id]: [...(pendingZones[zone.id] || []), item_id],
    });

    await toggleZoneItem(zone, type, item_id);
    handleZoneToggle();

    setPendingZones({
      ...pendingZones,
      [zone.id]: [
        ...(pendingZones[zone.id] || []).filter((id) => id !== item_id),
      ],
    });
  };

  const toggleRestriction = async (personal_inbox_id, token) => {
    if (!personal_inbox_id) return;

    if ((pendingRestrictions[token] || []).includes(personal_inbox_id)) return;

    setPendingRestrictions((prev) => ({
      ...prev,
      [token]: [...(prev[token] || []), personal_inbox_id],
    }));

    try {
      let updated = await inboxCtx.toggleInboxRestriction(personal_inbox_id, token);
      handleRestrictionToggle(updated);
    } finally {
      setPendingRestrictions((prev) => ({
        ...prev,
        [token]: (prev[token] || []).filter((id) => id !== personal_inbox_id),
      }));
    }
  };

  return (
    <>
      <section id="team-schedule-template">
        <div id="hms-page-title">
          <h1 className="uk-heading-small">Users</h1>
        </div>
        <div id="hms-main-body">
          <div className="uk-flex uk-flex-between uk-flex-top">
            <div className="hms-guest-btn">
              <button
                className="uk-button hms-btn hms-red-btn"
                uk-toggle="target: #team-add"
                onClick={handleAdd}
              >
                <span uk-icon="icon: plus; ratio: 0.7"></span> Invite User
              </button>
              <BulkHours
                selectedRecipients={selectedRecipients}
                inbox={inbox}
                setSelectedRecipients={setSelectedRecipients}
                inboxCtx={inboxCtx}
              />
            </div>
          </div>
          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              <Flex alignment="between top" className="guest-form-top-filters">
                <div>
                  <form
                    action=""
                    className="uk-form uk-flex uk-flex-middle hms-form"
                  >
                    <div className="uk-inline">
                      <div className="uk-search uk-search-default">
                        {criteria.search_term && criteria.search_term !== "" ? (
                          <a
                            className="uk-form-icon-flip uk-form-icon"
                            uk-icon="icon: close"
                            onClick={() =>
                              handleCriteriaChange("search_term", "")
                            }
                          ></a>
                        ) : (
                          <span
                            className="uk-search-icon-flip"
                            uk-search-icon="true"
                          />
                        )}
                        <input
                          className="uk-search-input"
                          type="search"
                          placeholder="Name"
                          value={criteria.search_term}
                          onChange={(event) =>
                            handleCriteriaChange(
                              "search_term",
                              event.target.value,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div
                      className="uk-flex-none uk-margin-left"
                      style={{ flex: "0 0 auto", width: "auto", maxWidth: "max-content" }}
                    >
                      <label
                        className="uk-flex uk-flex-middle uk-text-nowrap"
                        style={{ display: "inline-flex", width: "auto", marginBottom: 0 }}
                      >
                        <input
                          className="uk-checkbox"
                          type="checkbox"
                          style={{
                            width: 16,
                            minWidth: 16,
                            height: 16,
                            padding: 0,
                            margin: 0,
                            flex: "0 0 auto",
                            display: "inline-block",
                            verticalAlign: "middle",
                          }}
                          checked={showDeletedUsers}
                          onChange={(event) => {
                            handleCriteriaChange("page", 0);
                            setShowDeletedUsers(event.target.checked);
                          }}
                        />
                        <span className="uk-margin-small-left">Show Deleted Users</span>
                      </label>
                    </div>
                  </form>
                </div>
                <div>
                  <div className="hms-sortby-btn">
                    <button className="uk-button" type="button">
                      SORT BY<span uk-icon="icon: triangle-down"></span>
                    </button>
                    <div uk-dropdown="mode: click; offset: 0; animation: uk-animation-slide-top-small; duration: 400">
                      <ul className="uk-nav uk-dropdown-nav">
                        <li
                          className={classnames({
                            "uk-active": criteria.sort === "updated_on",
                          })}
                        >
                          <Link
                            href="#"
                            onClick={() =>
                              handleCriteriaChange("sort", "updated_on")
                            }
                          >
                            Last Updated
                          </Link>
                        </li>
                        <li
                          className={classnames({
                            "uk-active": criteria.sort === "first_name",
                          })}
                        >
                          <Link
                            href="#"
                            onClick={() =>
                              handleCriteriaChange("sort", "first_name")
                            }
                          >
                            First Name
                          </Link>
                        </li>
                        <li
                          className={classnames({
                            "uk-active": criteria.sort === "last_name",
                          })}
                        >
                          <Link
                            href="#"
                            onClick={() =>
                              handleCriteriaChange("sort", "last_name")
                            }
                          >
                            Last Name
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Flex>
              <div className="uk-margin-small-bottom">
              <a href="/settings/messages">Out-of-office Notification</a> Schedule: {groupAndSummarize()}.
              </div>
              <table className="uk-table uk-table-hover uk-table-divider hms-table hms-table-team">
                <thead>
                  <tr>
                    <th className="uk-table-shrink">
                      <input
                        type="checkbox"
                        checked={
                          pagedRecipients.filter((r) => !r.deleted).length > 0 &&
                          pagedRecipients
                            .filter((r) => !r.deleted)
                            .every((r) => selectedRecipients.includes(r.recipient_id))
                        }
                        onChange={toggleSelectAllOnPage}
                      />
                    </th>
                    <th className="uk-table-expand name-field">User</th>
                    <th className="uk-table-expand">Notification Schedule</th>
                    <th className="uk-table-expand">
                      Zones
                      <img
                        className="info-icon"
                        src="/images/info-icon.svg"
                        uk-tooltip="Restrict access to specific properties, contacts and messages."
                      />
                    </th>
                    <th className="uk-width-small">
                      Email Alerts{" "}
                      <img
                        className="info-icon"
                        src="/images/info-icon.svg"
                        alt="info"
                        uk-tooltip="Get email alerts for SMS delivery failures and missing home guides for upcoming bookings."
                      />
                    </th>
                    <th className="uk-width-small">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {inboxCtx.loading == true || !inbox
                    ? TableLoading(6)
                    : pagedRecipients.map((recipient) => {
                        let {
                          first_name,
                          last_name,
                          schedule,
                          primary_recipient,
                          email,
                          formatted_phone,
                          users,
                          user_id,
                          invitation,
                          deleted,
                        } = recipient;

                        let unregistered = !+user_id && !invitation;
                        const personalInboxes = Array.isArray(recipient.personal_inboxes)
                          ? recipient.personal_inboxes
                          : [];

                        const personal_inbox = personalInboxes[0] || null;

                        const personal_inbox_id = personal_inbox?.personal_inbox_id || null;
                        const restrictions = Array.isArray(personal_inbox?.restrictions)
                          ? personal_inbox.restrictions
                          : [];
                        const isDeletingRecipient =
                          inboxCtx.recipientPending &&
                          inboxCtx.deletedRecipient?.recipient_id === recipient.recipient_id;
                        const isUndeletingRecipient =
                          deleted &&
                          inboxCtx.recipientPending &&
                          inboxCtx.deletedRecipient?.recipient_id === recipient.recipient_id;

                        return (
                          <tr key={recipient.recipient_id}>
                            <td>
                              {!deleted && (
                                <input
                                  type="checkbox"
                                  checked={isSelected(recipient.recipient_id)}
                                  onChange={() => toggleRecipientSelection(recipient.recipient_id)}
                                />
                              )}
                            </td>
                            <td>
                              <div className="semi-bold-font">
                                {[first_name, last_name].join(" ")}{" "}
                              </div>
                              <div className="secondary">{email}</div>
                              <div className="uk-margin-small-top">
                                {deleted && (
                                  <div className="hms-booking-tag">Deleted</div>
                                )}
                                {!!primary_recipient && (
                                  <div className="hms-booking-tag">Admin</div>
                                )}
                                {!primary_recipient && !!+user_id && (
                                  <span className="hms-booking-tag">User</span>
                                )}
                                {unregistered && (
                                  <div className="hms-booking-tag">
                                    Unregistered
                                  </div>
                                )}
                                {!+user_id && !!invitation && (
                                  <div className="hms-booking-tag">Invited</div>
                                )}
                              </div>
                              <div className="uk-margin-top">
                                {!+user_id &&
                                  !invitation &&
                                  !deleted &&
                                  !primary_recipient &&
                                  (!inboxCtx.invitePending ||
                                    recipient.recipient_id !==
                                      rec?.recipient_id) && (
                                    <span
                                      className="hms-team-action"
                                      onClick={() => {
                                        handleInvite(recipient);
                                      }}
                                    >
                                      Click to Invite Again
                                    </span>
                                  )}
                                {!+user_id &&
                                  !invitation &&
                                  !deleted &&
                                  inboxCtx.invitePending &&
                                  recipient.recipient_id ===
                                    rec?.recipient_id && (
                                    <span className="hms-team-action">
                                      Inviting...
                                    </span>
                                  )}
                                {!+user_id && !!invitation && (
                                  <span>Invited</span>
                                )}
                              </div>

                              {Array.isArray(users) && users.length > 0 && users[0]?.last_login_at && (
                                <div className="secondary uk-margin-top">
                                  {"Last Login: " +
                                    moment
                                      .utc(users[0].last_login_at)
                                      .local()
                                      .format("MM/DD/YYYY hh:mmA")}
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="schedule-summary">
                                {summarizeSchedule(schedule).map((g) => (
                                  <div key={`${g.label}-${g.range}`} className="schedule-line">
                                    <span className="schedule-days">{g.label}:</span>
                                    <span className="schedule-time">{g.range}</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td className="hms-add-tag">
                              <div className="uk-flex uk-flex-top">
                                <div className="hms-add-tag">
                                  <button
                                    className="uk-button"
                                    type="button"
                                    aria-haspopup="true"
                                    aria-expanded="false"
                                  >
                                    <img src="/images/group.svg" />
                                  </button>
                                  <div
                                    data-uk-dropdown="mode: click"
                                    className="guest-tags-dropdown uk-dropdown"
                                  >
                                    <ul className="uk-nav uk-dropdown-nav tags-selector">
                                      {/* ---- Special Cases ---- */}
                                      <li>
                                        <label>
                                          <input
                                            className="uk-checkbox"
                                            type="checkbox"
                                            value="known"
                                            disabled={
                                              !personal_inbox_id ||
                                              pendingRestrictions[
                                                "known"
                                              ].includes(personal_inbox_id)
                                            }
                                            checked={
                                              !restrictions.includes("known")
                                            }
                                            onChange={() =>
                                              toggleRestriction(
                                                personal_inbox_id,
                                                "known",
                                              )
                                            }
                                          />
                                          Unknown Numbers
                                        </label>
                                      </li>
                                      <li>
                                        <label>
                                          <input
                                            className="uk-checkbox"
                                            type="checkbox"
                                            value="ignore-webchat"
                                            disabled={
                                              !personal_inbox_id ||
                                              pendingRestrictions[
                                                "ignore-webchat"
                                              ].includes(personal_inbox_id)
                                            }
                                            checked={
                                              !restrictions.includes(
                                                "ignore-webchat",
                                              )
                                            }
                                            onChange={() =>
                                              toggleRestriction(
                                                personal_inbox_id,
                                                "ignore-webchat",
                                              )
                                            }
                                          />
                                          Webchat Messages (Leads)
                                        </label>
                                      </li>
                                      {/* ---- End Special Cases ---- */}
                                      {(zoneGroups?.inboxes || []).map(
                                        (zone) => {
                                          return (
                                            <li key={zone.id}>
                                              <label>
                                                <input
                                                  className="uk-checkbox"
                                                  type="checkbox"
                                                  value={zone.id}
                                                  disabled={
                                                    !personal_inbox_id ||
                                                    (
                                                      pendingZones[zone.id] ||
                                                      []
                                                    ).includes(
                                                      personal_inbox_id,
                                                    )
                                                  }
                                                  checked={(
                                                    zoneInboxes[zone.id] || []
                                                  ).includes(personal_inbox_id)}
                                                  onChange={() =>
                                                    toggleZone(
                                                      zone,
                                                      "inbox",
                                                      personal_inbox_id,
                                                    )
                                                  }
                                                />
                                                {zone.label}
                                              </label>
                                            </li>
                                          );
                                        },
                                      )}
                                    </ul>
                                  </div>
                                </div>
                                <div className="hms-guest-tags">
                                  <div className="guest-tags">
                                    {!!personal_inbox_id &&
                                      !restrictions.includes("known") && (
                                        <div className="uk-inline zone">
                                          Unknown Numbers
                                        </div>
                                      )}
                                    {!!personal_inbox_id &&
                                      !restrictions.includes(
                                        "ignore-webchat",
                                      ) && (
                                        <div className="uk-inline zone">
                                          Webchat (Leads)
                                        </div>
                                      )}
                                    {(zoneGroups?.inboxes || []).map((zone) => {
                                      return (
                                        (
                                          zone.personal_inbox_ids || []
                                        ).includes(personal_inbox_id) && (
                                          <div key={zone.id} className="uk-inline zone">
                                            {zone.label}
                                          </div>
                                        )
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td>
                              {!deleted && (
                                <div className="autosend-checkbox">
                                  <label
                                    htmlFor={recipient.recipient_id}
                                    className="setting-switch"
                                  >
                                    <input
                                      type="checkbox"
                                      id={recipient.recipient_id}
                                      onChange={(e) =>
                                        handleAlertToggle(e, recipient)
                                      }
                                      checked={recipient.alerts}
                                    />
                                    <span className="setting-slider setting-round"></span>
                                  </label>
                                </div>
                              )}
                            </td>
                            <td className="hms-action-btn">
                              {!deleted && (
                                <div className="uk-flex uk-flex-top uk-flex-between">
                                  <div className="uk-flex">
                                    <div>
                                      <button
                                        className="uk-button"
                                        uk-toggle="target: #team-add"
                                        onClick={() => handleEdit(recipient)}
                                        uk-tooltip="Edit"
                                      >
                                        <Image src="/images/edit.svg" />
                                      </button>
                                    </div>
                                    {!recipient.primary_recipient && (
                                      <div>
                                        {isDeletingRecipient ? (
                                          <span
                                            uk-spinner="ratio: 0.6"
                                            aria-label="Deleting user"
                                          />
                                        ) : (
                                          <button
                                            className="uk-button"
                                            uk-toggle="target: #team-delete"
                                            onClick={() =>
                                              handleDelete(recipient)
                                            }
                                            uk-tooltip="Delete"
                                          >
                                            <Image src="/images/delete.svg" />
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              {deleted && (
                                <div>
                                  {isUndeletingRecipient ? (
                                    <span
                                      uk-spinner="ratio: 0.6"
                                      aria-label="Undeleting user"
                                    />
                                  ) : (
                                    <button
                                      className="uk-button"
                                      onClick={() => handleUnDelete(recipient)}
                                      uk-tooltip="Undelete"
                                      aria-label="Undelete"
                                    >
                                      <Image src="/images/undelete.svg" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                </tbody>
              </table>
            </div>
          </div>
          <Flex alignment="between top" className="guest-form-top-filters">
            <Pagination
              count={recipients.length}
              index={criteria.page}
              onIndexChanged={handleIndexChanged}
            />
            {recipients.length > 0 && <RecordsPerPage />}
          </Flex>

          <div className="hms-small-heading">
            <h3>Team Availability</h3>
          </div>

          <div className="uk-card uk-margin-bottom">
            <div className="uk-overflow-auto">
              <div className="uk-flex uk-flex-middle uk-flex-center team-availability-wrapper">
                <div className="team-availability-time">
                  <div>SUNDAY</div>
                  <div>{getTeamSchedule(1)}</div>
                </div>
                <div className="team-availability-time">
                  <div>MONDAY</div>
                  <div>{getTeamSchedule(2)}</div>
                </div>
                <div className="team-availability-time">
                  <div>TUESDAY</div>
                  <div>{getTeamSchedule(3)}</div>
                </div>
                <div className="team-availability-time">
                  <div>WEDNESDAY</div>
                  <div>{getTeamSchedule(4)}</div>
                </div>
                <div className="team-availability-time">
                  <div>THURSDAY</div>
                  <div>{getTeamSchedule(5)}</div>
                </div>
                <div className="team-availability-time">
                  <div>FRIDAY</div>
                  <div>{getTeamSchedule(6)}</div>
                </div>
                <div className="team-availability-time">
                  <div>SATURDAY</div>
                  <div>{getTeamSchedule(7)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <TeamAdd />
      <TeamDelete />
    </>
  );
};

export default Team;
